import React from 'react';
import { Box, Paper, Typography, Divider, IconButton, List, ListItem, ListItemText } from '@mui/material';
import { Close } from '@mui/icons-material';

export default function EvaluationFeedbackPanel({ feedback, onClose }) {
  if (!feedback) return null;

  // Parse feedback if it's a string
  let parsedFeedback = feedback;
  if (typeof feedback === 'string') {
    try {
      parsedFeedback = JSON.parse(feedback);
    } catch (e) {
      // If parsing fails, show raw feedback
      return (
        <Paper 
          elevation={1} 
          sx={{ 
            p: 3, 
            mt: 2, 
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1 }}>
            Evaluation Feedback
          </Typography>
          <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7 }}>{feedback}</Typography>
        </Paper>
      );
    }
  }

  // Helper function to convert text to bullet points
  const textToBulletPoints = (text) => {
    if (!text || typeof text !== 'string') return [];
    
    // First, try splitting by existing bullet points or numbered lists
    const bulletSplit = text.split(/(?:\n\s*[-•*]\s*)|(?:\n\s*\d+[\.\)]\s*)/);
    if (bulletSplit.length > 1) {
      return bulletSplit
        .map(line => line.trim())
        .filter(line => line.length > 0);
    }
    
    // If no bullets found, split by newlines
    const lineSplit = text.split(/\n+/);
    if (lineSplit.length > 1) {
      return lineSplit
        .map(line => line.trim())
        .filter(line => line.length > 0);
    }
    
    // If single paragraph, split by sentences (period + space or period + newline)
    const sentences = text.split(/(?<=[.!?])\s+/);
    if (sentences.length > 1) {
      return sentences
        .map(sentence => sentence.trim())
        .filter(sentence => sentence.length > 0);
    }
    
    // If all else fails, return as single item
    return [text.trim()].filter(item => item.length > 0);
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        backgroundColor: '#fff',
        borderRadius: 0,
        border: 'none',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        pb: 2,
        borderBottom: '2px solid #e0e0e0',
        flexShrink: 0
      }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
          Evaluation Results
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onClose && (
            <IconButton 
              onClick={onClose}
              size="small"
              sx={{ 
                color: '#555',
                '&:hover': { 
                  backgroundColor: '#f0f0f0',
                  color: '#222'
                }
              }}
            >
              <Close />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        pr: 1,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#555',
        }
      }}>

      {parsedFeedback.assessment && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1.5 }}>
            Assessment
          </Typography>
          <List dense disablePadding>
            {textToBulletPoints(parsedFeedback.assessment).map((point, index) => (
              <ListItem key={index} sx={{ pl: 0, py: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                <Typography component="span" sx={{ mr: 1.5, color: '#222', fontWeight: 'bold', flexShrink: 0, minWidth: '24px' }}>
                  {index + 1}.
                </Typography>
                <Typography variant="body2" sx={{ color: '#222', lineHeight: 1.6, flex: 1 }}>
                  {point}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {(parsedFeedback.mistakes && parsedFeedback.mistakes.length > 0) || parsedFeedback.mistakesRaw ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1.5 }}>
            Mistakes
          </Typography>
          {parsedFeedback.mistakes && parsedFeedback.mistakes.length > 0 ? (
            <List dense disablePadding>
              {parsedFeedback.mistakes.map((mistake, index) => (
                <ListItem key={index} sx={{ pl: 0, py: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                  <Typography component="span" sx={{ mr: 1.5, color: '#333', fontWeight: 'bold', flexShrink: 0, minWidth: '24px' }}>
                    {index + 1}.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.6, flex: 1 }}>
                    {(mistake.entity_name || mistake.class_name || mistake.title) 
                      ? `${mistake.entity_name || mistake.class_name || mistake.title}: ${mistake.description}`
                      : mistake.description}
                    {mistake.suggestion && ` (Suggestion: ${mistake.suggestion})`}
                  </Typography>
                </ListItem>
              ))}
            </List>
          ) : (
            <List dense disablePadding>
              {textToBulletPoints(parsedFeedback.mistakesRaw).map((point, index) => (
                <ListItem key={index} sx={{ pl: 0, py: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                  <Typography component="span" sx={{ mr: 1.5, color: '#333', fontWeight: 'bold', flexShrink: 0, minWidth: '24px' }}>
                    {index + 1}.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.6, flex: 1 }}>
                    {point}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      ) : null}

      {(parsedFeedback.strengths && parsedFeedback.strengths.length > 0) || parsedFeedback.strengthsRaw ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1.5 }}>
            Strengths
          </Typography>
          {parsedFeedback.strengths && parsedFeedback.strengths.length > 0 ? (
            <List dense disablePadding>
              {parsedFeedback.strengths.map((strength, index) => (
                <ListItem key={index} sx={{ pl: 0, py: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                  <Typography component="span" sx={{ mr: 1.5, color: '#333', fontWeight: 'bold', flexShrink: 0, minWidth: '24px' }}>
                    {index + 1}.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.6, flex: 1 }}>
                    {strength.description}
                  </Typography>
                </ListItem>
              ))}
            </List>
          ) : (
            <List dense disablePadding>
              {textToBulletPoints(parsedFeedback.strengthsRaw).map((point, index) => (
                <ListItem key={index} sx={{ pl: 0, py: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                  <Typography component="span" sx={{ mr: 1.5, color: '#333', fontWeight: 'bold', flexShrink: 0, minWidth: '24px' }}>
                    {index + 1}.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.6, flex: 1 }}>
                    {point}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      ) : null}

      {parsedFeedback.codeAreasNeedingImprovement && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1.5 }}>
            Code Areas Needing Improvement
          </Typography>
          <List dense disablePadding>
            {textToBulletPoints(parsedFeedback.codeAreasNeedingImprovement).map((point, index) => (
              <ListItem key={index} sx={{ pl: 0, py: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                <Typography component="span" sx={{ mr: 1.5, color: '#d32f2f', fontWeight: 'bold', flexShrink: 0, minWidth: '24px' }}>
                  {index + 1}.
                </Typography>
                <Typography variant="body2" sx={{ color: '#222', lineHeight: 1.6, flex: 1 }}>
                  {point}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {parsedFeedback.missingDesignPatterns && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1.5 }}>
            Missing Design Patterns
          </Typography>
          <List dense disablePadding>
            {textToBulletPoints(parsedFeedback.missingDesignPatterns).map((point, index) => (
              <ListItem key={index} sx={{ pl: 0, py: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                <Typography component="span" sx={{ mr: 1.5, color: '#ed6c02', fontWeight: 'bold', flexShrink: 0, minWidth: '24px' }}>
                  {index + 1}.
                </Typography>
                <Typography variant="body2" sx={{ color: '#222', lineHeight: 1.6, flex: 1 }}>
                  {point}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {parsedFeedback.designPatternOpportunities && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1.5 }}>
            Design Pattern Opportunities
          </Typography>
          <List dense disablePadding>
            {textToBulletPoints(parsedFeedback.designPatternOpportunities).map((point, index) => (
              <ListItem key={index} sx={{ pl: 0, py: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                <Typography component="span" sx={{ mr: 1.5, color: '#1976d2', fontWeight: 'bold', flexShrink: 0, minWidth: '24px' }}>
                  {index + 1}.
                </Typography>
                <Typography variant="body2" sx={{ color: '#222', lineHeight: 1.6, flex: 1 }}>
                  {point}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {parsedFeedback.missingFeatures && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1.5 }}>
            Missing Features
          </Typography>
          <List dense disablePadding>
            {textToBulletPoints(parsedFeedback.missingFeatures).map((point, index) => (
              <ListItem key={index} sx={{ pl: 0, py: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                <Typography component="span" sx={{ mr: 1.5, color: '#9c27b0', fontWeight: 'bold', flexShrink: 0, minWidth: '24px' }}>
                  {index + 1}.
                </Typography>
                <Typography variant="body2" sx={{ color: '#222', lineHeight: 1.6, flex: 1 }}>
                  {point}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {parsedFeedback.learning && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1.5 }}>
            Learning
          </Typography>
          <List dense disablePadding>
            {textToBulletPoints(parsedFeedback.learning).map((point, index) => (
              <ListItem key={index} sx={{ pl: 0, py: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                <Typography component="span" sx={{ mr: 1.5, color: '#222', fontWeight: 'bold', flexShrink: 0, minWidth: '24px' }}>
                  {index + 1}.
                </Typography>
                <Typography variant="body2" sx={{ color: '#222', lineHeight: 1.6, flex: 1 }}>
                  {point}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
      </Box>
    </Paper>
  );
}

